<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('expenseItem.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('expenseItem.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="ts-justify-end">
            <Tooltip
              :content="$t('expenseItem.search')"
              placement="top"
              transfer
              :max-width="width"
            >
              <Input
                search
                v-model="search"
                :placeholder="$t('expenseItem.search')"
                :style="width"
                @on-focus="() => (width = 'width: 500px')"
                @on-blur="() => (width = 'width: 300px')"
              />
            </Tooltip>
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :columns="columns"
        :data="groupResources"
        :span-method="handleSpan"
        row-key="index"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
        <template #uom_category="{ row, index }">
          {{ row.uom_category ? row.uom_category.uom_cat_name_en : '' }}
        </template>
        <template #action="{ row, index }">
          <a
            href="#"
            class="text-primary"
            @click.prevent="edit(row)"
            v-tooltip="$t('edit')"
          >
            <Icon type="ios-create" size="20" />
          </a>
          <Tooltip :content="$t('delete')" placement="top" transfer>
            <Poptip
              confirm
              :title="$t('areYouSure')"
              @on-ok="deleteRecord(row)"
              :transfer="true"
              :ok-text="$t('yes')"
              :cancel-text="$t('no')"
            >
              <a class="text-danger" :disabled="row._deleting">
                <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                <Icon type="ios-trash" size="20" v-else />
              </a> </Poptip
          ></Tooltip>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
      <!-- form-action -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('expenseItem.pageTitle')"
        centered
        @cancel="clearEdit"
        :zIndex="1020"
        :footer="null"
      >
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import iTable from '@/store/i-table-helper'

export default {
  name: 'expenseItemIndex',
  components: {
    FromAction
  },
  data() {
    return {
      loading: false,
      showForm: false,
      width: 'width: 300px'
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('expense/expenseItem', ['resources', 'pagination']),
    groupResources() {
      return iTable.rowGroup(this.resources, 'exp_category')
    },
    search: {
      get() {
        return this.$store.state.expense.expenseItem.search
      },
      set(newValue) {
        this.$store.commit('expense/expenseItem/SET_SEARCH', newValue)
        this.$store.commit('expense/expenseItem/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          key: 'column_name',
          tree: true,
          maxWidth: 15
        },
        {
          title: this.$t('expenseItem.expenseCode'),
          key: 'exp_code'
        },
        {
          title: this.$t('expenseItem.expenseItem'),
          key: 'exp_item'
        },
        {
          title: this.$t('expenseItem.expenseDescription'),
          key: 'exp_desc'
        },
        {
          title: this.$t('expenseItem.uomCategory'),
          slot: 'uom_category',
          align: 'center'
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 90
        }
      ]
    }
  },
  methods: {
    handleSpan({ row, column, rowIndex, columnIndex }) {
      return iTable.handleSpan(row, columnIndex, this.columns)
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('expense/expenseItem/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('expense/expenseItem/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit() {
      this.$store.commit('expense/expenseItem/SET_EDIT_DATA', {})
      this.showForm = false
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('expense/expenseItem/destroy', record.exp_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('expense/expenseItem/RESET_STATE')
    next()
  }
}
</script>
<style>
.ivu-table-cell-tree {
  margin-right: 10px;
}
.ivu-table-small i {
  font-size: 14px;
}
</style>
